import { useMemo, useState, memo, type MouseEvent } from 'react';
import { NavLink } from 'react-router-dom';
import {
  CNavItem,
  CNavLink,
  CSidebar,
  CSidebarFooter,
  CSidebarHeader,
  CSidebarNav,
  CSidebarToggler,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { useSidebar } from 'contexts/AppContext';
import useIsMobile from 'hooks/useIsMobile';
import theme from 'config/theme';
import { useIntl } from 'react-intl';
import { routePatterns } from 'router/route-paths';
import sidebarItems, { type ISidebarItem } from './sidebarItems';
import sidebarVisible, { showSidebarAfterVisibleChange } from './Sidebar.utils';

const { zIndexSidebar } = theme;

interface ISidebarLinkProps {
  item: Omit<ISidebarItem, 'hide'>;
}

/** A router link (active on its exact path), or for an item without a path a link that only calls `onClick`. */
const SidebarLink = ({ item: { name, to, icon, onClick } }: ISidebarLinkProps) => {
  const content = (
    <>
      <CIcon icon={icon} customClassName="nav-icon" />
      {name}
    </>
  );
  if (to) {
    return (
      <CNavLink as={NavLink} to={to} exact activeClassName="active">
        {content}
      </CNavLink>
    );
  }
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    onClick?.();
  };
  return (
    <CNavLink href="#" onClick={handleClick}>
      {content}
    </CNavLink>
  );
};

const Sidebar = () => {
  const { showSidebar, syncSidebar } = useSidebar();
  const mobile = useIsMobile();
  // CoreUI 3's minimizer: the narrow sidebar that unfolds on hover.
  const [unfoldable, setUnfoldable] = useState(false);
  const { formatMessage: t } = useIntl();

  const localizedSidebarItems = useMemo(
    () =>
      sidebarItems
        .filter(({ hide }) => !hide)
        .map(({ name, hide, ...rest }) => ({ name: t({ id: name }), ...rest })),
    [t],
  );

  // Bug: below the lg breakpoint CoreUI's backdrop (z-index 1034) lies over the open sidebar (z-index 10 here), so a
  // click on a link hits the backdrop: the sidebar closes and the page does not change; CoreUI 3 did the same.
  return (
    <CSidebar
      colorScheme="dark"
      position="fixed"
      unfoldable={unfoldable}
      visible={sidebarVisible(showSidebar, mobile)}
      onVisibleChange={(visible: boolean) =>
        syncSidebar(showSidebarAfterVisibleChange(showSidebar, visible, mobile))
      }
      style={{ zIndex: zIndexSidebar }}
    >
      <CSidebarHeader className="d-none d-lg-flex">
        <NavLink className="sidebar-brand" to={routePatterns.projects.stringify({ page: 1 })} />
      </CSidebarHeader>
      <CSidebarNav>
        {localizedSidebarItems.map((item) => (
          <CNavItem key={item.name} className={item.className}>
            <SidebarLink item={item} />
          </CNavItem>
        ))}
      </CSidebarNav>
      <CSidebarFooter className="d-none d-lg-flex">
        <CSidebarToggler onClick={() => setUnfoldable(!unfoldable)} />
      </CSidebarFooter>
    </CSidebar>
  );
};

export default memo(Sidebar);
